/**
 * @module useWorkstations
 * @description React Query hooks for workstation operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import { API_ENDPOINTS } from '../config/api.config.js';

/**
 * Hook to authenticate a workstation user
 * @returns {Object} Mutation result for authenticating workstation user
 */
export const useAuthenticateWorkstation = () => {
  return useMutation({
    mutationFn: async (credentials) => {
      const { data } = await api.post(`${API_ENDPOINTS.AUTH}/workstations/authenticate`, credentials);
      return data;
    },
  });
};

/**
 * Hook to get workstations list
 * @param {Object} filters - Optional filters for workstations
 * @returns {Object} Query result with workstations data
 */
export const useWorkstations = (filters = {}) => {
  return useQuery({
    queryKey: ['workstations', filters],
    queryFn: async () => {
      const { data } = await api.get(`${API_ENDPOINTS.WORKSTATIONS || API_ENDPOINTS.AUTH}/workstations`, { params: filters });
      return data;
    },
  });
};

/**
 * Hook to get a single workstation by ID
 * @param {string} workstationId - Workstation ID
 * @returns {Object} Query result with workstation data
 */
export const useWorkstation = (workstationId) => {
  return useQuery({
    queryKey: ['workstations', workstationId],
    queryFn: async () => {
      const { data } = await api.get(`${API_ENDPOINTS.WORKSTATIONS || API_ENDPOINTS.AUTH}/workstations/${workstationId}`);
      return data;
    },
    enabled: !!workstationId,
  });
};

/**
 * Hook to update a workstation
 * @returns {Object} Mutation result for updating workstation
 */
export const useUpdateWorkstation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ workstationId, data }) => {
      const response = await api.put(`${API_ENDPOINTS.WORKSTATIONS || API_ENDPOINTS.AUTH}/workstations/${workstationId}`, data);
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['workstations', variables.workstationId] });
      queryClient.invalidateQueries({ queryKey: ['workstations'] });
    },
  });
};

/**
 * Hook to create a workstation
 * @returns {Object} Mutation result for creating workstation
 */
export const useCreateWorkstation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (workstationData) => {
      const { data } = await api.post(`${API_ENDPOINTS.WORKSTATIONS || API_ENDPOINTS.AUTH}/workstations`, workstationData);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workstations'] });
    },
  });
};

/**
 * Hook to delete a workstation
 * @returns {Object} Mutation result for deleting workstation
 */
export const useDeleteWorkstation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (workstationId) => {
      const { data } = await api.delete(`${API_ENDPOINTS.WORKSTATIONS || API_ENDPOINTS.AUTH}/workstations/${workstationId}`);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workstations'] });
    },
  });
};

export const useWorkstationConfig = (workstationId) => {
  return useQuery({
    queryKey: ['workstations', 'config', workstationId],
    queryFn: async () => {
      const { data } = await api.get(`${API_ENDPOINTS.AUTH}/workstations/${workstationId}`);
      return data;
    },
    enabled: !!workstationId,
  });
};

export const useWorkstationsByZoneAndType = (zoneId, type) => {
  return useQuery({
    queryKey: ['workstations', 'zone', zoneId, 'type', type],
    queryFn: async () => {
      const { data } = await api.get(`${API_ENDPOINTS.AUTH}/workstations`, { params: { zone: zoneId, type } });
      return data.workstations || [];
    },
    enabled: !!zoneId && !!type,
  });
};

/**
 * Hook to get workstation monitoring status (real-time metrics from daemons)
 * @param {Object} options - Query options including refetchInterval
 * @returns {Object} Query result with monitoring status data
 */
export const useWorkstationMonitoringStatus = (options = {}) => {
  return useQuery({
    queryKey: ['workstations', 'monitoring', 'status'],
    queryFn: async () => {
      const { data } = await api.get(`${API_ENDPOINTS.WORKSTATIONS}/monitoring/status`);
      return data;
    },
    retry: false,
    refetchInterval: options.refetchInterval || 10000,
    ...options,
  });
};

/**
 * Hook to get detailed metrics for a specific workstation
 * @param {string} stationCode - Workstation station code
 * @returns {Object} Query result with workstation metrics
 */
export const useWorkstationMetrics = (stationCode) => {
  return useQuery({
    queryKey: ['workstations', 'monitoring', 'metrics', stationCode],
    queryFn: async () => {
      const { data } = await api.get(`${API_ENDPOINTS.WORKSTATIONS}/monitoring/metrics/${stationCode}`);
      return data;
    },
    enabled: !!stationCode,
    refetchInterval: 5000,
  });
};

/**
 * Hook to get remediation events
 * @param {Object} filters - Optional filters (station_code, limit)
 * @returns {Object} Query result with remediation events
 */
export const useRemediationEvents = (filters = {}) => {
  return useQuery({
    queryKey: ['workstations', 'monitoring', 'events', filters],
    queryFn: async () => {
      const { data } = await api.get(`${API_ENDPOINTS.WORKSTATIONS}/monitoring/events`, { params: filters });
      return data;
    },
    refetchInterval: 15000,
  });
};

/**
 * Hook to get hardware escalations
 * @param {Object} filters - Optional filters (resolved, station_code)
 * @returns {Object} Query result with hardware escalations
 */
export const useHardwareEscalations = (filters = {}) => {
  return useQuery({
    queryKey: ['workstations', 'monitoring', 'escalations', filters],
    queryFn: async () => {
      const { data } = await api.get(`${API_ENDPOINTS.WORKSTATIONS}/monitoring/escalations`, { params: filters });
      return data;
    },
    refetchInterval: 30000,
  });
};

/**
 * Hook to send a remediation command to a workstation
 * @returns {Object} Mutation result for sending remediation command
 */
export const useSendRemediationCommand = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (command) => {
      const { data } = await api.post(`${API_ENDPOINTS.WORKSTATIONS}/monitoring/command`, command);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workstations', 'monitoring'] });
    },
  });
};

/**
 * Hook to resolve a hardware escalation
 * @returns {Object} Mutation result for resolving escalation
 */
export const useResolveEscalation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ escalationId, resolutionNotes }) => {
      const { data } = await api.post(
        `${API_ENDPOINTS.WORKSTATIONS}/monitoring/escalations/${escalationId}/resolve`,
        { resolution_notes: resolutionNotes }
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workstations', 'monitoring', 'escalations'] });
    },
  });
};

// =============================================================================
// AI ACTION DASHBOARD HOOKS
// =============================================================================

/**
 * Hook to get AI action logs for dashboard visibility
 * @param {Object} filters - Optional filters (workstation_id, action_type, limit)
 * @returns {Object} Query result with AI action logs
 */
export const useAIActionLogs = (filters = {}) => {
  return useQuery({
    queryKey: ['workstations', 'dashboard', 'actions', filters],
    queryFn: async () => {
      const { data } = await api.get(`${API_ENDPOINTS.WORKSTATIONS}/dashboard/actions`, { params: filters });
      return data;
    },
    refetchInterval: 5000, // Real-time updates
  });
};

/**
 * Hook to get AI action statistics
 * @returns {Object} Query result with AI action stats
 */
export const useAIActionStats = () => {
  return useQuery({
    queryKey: ['workstations', 'dashboard', 'stats'],
    queryFn: async () => {
      const { data } = await api.get(`${API_ENDPOINTS.WORKSTATIONS}/dashboard/stats`);
      return data;
    },
    refetchInterval: 10000,
  });
};

/**
 * Hook to get workstations overview for dashboard
 * @returns {Object} Query result with workstations overview
 */
export const useWorkstationsDashboard = () => {
  return useQuery({
    queryKey: ['workstations', 'dashboard', 'workstations'],
    queryFn: async () => {
      const { data } = await api.get(`${API_ENDPOINTS.WORKSTATIONS}/dashboard/workstations`);
      return data;
    },
    refetchInterval: 10000,
  });
};

/**
 * Hook to reboot a workstation (full OS reboot)
 * @returns {Object} Mutation result for rebooting workstation
 */
export const useRebootWorkstation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ workstationId, reason = 'Admin requested' }) => {
      const { data } = await api.post(`${API_ENDPOINTS.WORKSTATIONS}/control/reboot`, {
        workstation_id: workstationId,
        reason,
      });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workstations'] });
    },
  });
};

/**
 * Hook to restart workstation agent (software only)
 * @returns {Object} Mutation result for restarting agent
 */
export const useRestartAgent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ workstationId, reason = 'Admin requested' }) => {
      const { data } = await api.post(`${API_ENDPOINTS.WORKSTATIONS}/control/restart-agent`, {
        workstation_id: workstationId,
        reason,
      });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workstations'] });
    },
  });
};

export const useTaskTrackingStatistics = (options = {}) => {
  return useQuery({
    queryKey: ['workstations', 'task-tracking', 'statistics'],
    queryFn: async () => {
      const { data } = await api.get(`${API_ENDPOINTS.WORKSTATIONS}/monitoring/task-tracking/statistics`);
      return data;
    },
    retry: false,
    ...options,
  });
};

export const useTaskTrackingEvents = (filters = {}, options = {}) => {
  return useQuery({
    queryKey: ['workstations', 'task-tracking', 'events', filters],
    queryFn: async () => {
      const { data } = await api.get(`${API_ENDPOINTS.WORKSTATIONS}/monitoring/task-tracking/events`, { params: filters });
      return data;
    },
    retry: false,
    ...options,
  });
};

export const useWorkstationLogs = (filters = {}, options = {}) => {
  return useQuery({
    queryKey: ['workstations', 'logs', filters],
    queryFn: async () => {
      const { data } = await api.get(`${API_ENDPOINTS.WORKSTATIONS}/logs`, { params: filters });
      return data;
    },
    retry: false,
    ...options,
  });
};

export const useTransportLabels = (options = {}) => {
  return useQuery({
    queryKey: ['workstations', 'transport-labels'],
    queryFn: async () => {
      const { data } = await api.get(`${API_ENDPOINTS.WORKSTATIONS}/sync/transport_labels`);
      return data;
    },
    retry: false,
    ...options,
  });
};

